import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { API_ENDPOINTS } from "../../config/api";
import { formatDate } from "../../utils/dateFormatting";

import {
  LoadingState,
  EmptyState,
  ErrorState
} from "../../components/CommonStates";
import Modal, {
  ModalTitle,
  ModalSection,
  ModalLabel,
  ModalValue,
  ModalBody,
  ModalText
} from "../../components/Modal";
import { Table, Row, Column } from "../../components/Table";
import Button from "../../components/Button";

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const SummaryCard = styled.div`
  background: white;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  padding: 1.5rem;
  text-align: center;

  .number {
    font-size: 2.5rem;
    font-weight: 700;
    color: #007bff;
    margin-bottom: 1rem;
  }

  .label {
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .percentage {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  .positive {
    color: #28a745;
  }

  .negative {
    color: #dc3545;
  }

  .neutral {
    color: #6c757d;
  }
`;

const Text = styled.div`
  max-width: 200px;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1rem;
`;

const PaginationButton = styled.button`
  background: ${(props) => (props.$active ? "#007bff" : "white")};
  color: ${(props) => (props.$active ? "white" : "#007bff")};
  border: 0.125rem solid #007bff;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;

  &:hover:not(:disabled) {
    background: ${(props) => (props.$active ? "#0056b3" : "#f8f9fa")};
  }

  &:disabled {
    background: #e9ecef;
    color: #6c757d;
    border-color: #e9ecef;
    cursor: not-allowed;
  }
`;

const getRatingDisplay = (rating) => {
  if (rating === true) {
    return <span className="positive">👍</span>;
  } else if (rating === false) {
    return <span className="negative">👎</span>;
  } else {
    return <span className="neutral"></span>;
  }
};

function FeedbackTab({ onError, onSuccess }) {
  const [feedback, setFeedback] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [limit] = useState(50);
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const handleRowClick = (item) => {
    setSelectedFeedback(item);
  };

  const handleCloseModal = () => {
    setSelectedFeedback(null);
  };

  const fetchFeedback = useCallback(
    async (offset = 0) => {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_ENDPOINTS.adminFeedback}?limit=${limit}&offset=${offset}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );

        const data = await response.json();

        if (response.ok) {
          setFeedback(data.feedback || []);
          setSummary(data.summary || null);
          setPagination(data.pagination || null);
        } else {
          setError(data.error || "Failed to fetch feedback");
        }
      } catch (error) {
        setError("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [limit]
  );

  const handlePageChange = (newOffset) => {
    setCurrentPage(newOffset / limit);
    fetchFeedback(newOffset);
  };

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  return (
    <>
      {error && <ErrorState>{error}</ErrorState>}

      {summary && (
        <SummaryCards>
          <SummaryCard>
            <div className="number">{summary.totalMessages}</div>
            <div className="label">Ratings & feedback</div>
          </SummaryCard>
          <SummaryCard>
            <div className="number positive">{summary.thumbsUpCount}</div>
            <div className="label">
              Thumbs up ({summary.thumbsUpPercentage}%)
            </div>
          </SummaryCard>
          <SummaryCard>
            <div className="number negative">{summary.thumbsDownCount}</div>
            <div className="label">
              Thumbs down ({summary.thumbsDownPercentage}%)
            </div>
          </SummaryCard>
          <SummaryCard>
            <div className="number neutral">{summary.noRatingCount}</div>
            <div className="label">No rating</div>
          </SummaryCard>
          <SummaryCard>
            <div className="number">{summary.feedbackCount}</div>
            <div className="label">With feedback</div>
          </SummaryCard>
        </SummaryCards>
      )}

      {loading && !feedback.length ? (
        <LoadingState>Loading feedback...</LoadingState>
      ) : feedback.length === 0 ? (
        <EmptyState>
          <h4>No feedback found</h4>
          <p>
            User feedback will appear here once users start rating messages.
          </p>
        </EmptyState>
      ) : (
        <>
          <Table columns="1fr 1fr 0.5fr 1fr 1fr">
            <Row>
              <Column>Bot</Column>
              <Column>User</Column>
              <Column>Rating</Column>
              <Column>Feedback</Column>
              <Column>Date</Column>
            </Row>

            {feedback.map((item) => (
              <Row key={item.id} onClick={() => handleRowClick(item)}>
                <Column data-label="Bot">{item.botName || item.botId}</Column>
                <Column data-label="User">{item.username}</Column>
                <Column data-label="Rating">
                  {getRatingDisplay(item.rating)}
                </Column>
                <Column data-label="Feedback">
                  {item.feedback && (
                    <Text title={item.feedback}>{item.feedback}</Text>
                  )}
                </Column>
                <Column data-label="Date">{formatDate(item.createdAt)}</Column>
              </Row>
            ))}
          </Table>

          {pagination && pagination.hasMore && (
            <Pagination>
              <PaginationButton
                onClick={() =>
                  handlePageChange(Math.max(0, currentPage * limit - limit))
                }
                disabled={currentPage === 0}
              >
                Previous
              </PaginationButton>
              <span>
                Page {currentPage + 1} of {Math.ceil(pagination.total / limit)}
              </span>
              <PaginationButton
                onClick={() => handlePageChange((currentPage + 1) * limit)}
                disabled={!pagination.hasMore}
              >
                Next
              </PaginationButton>
            </Pagination>
          )}
        </>
      )}

      {selectedFeedback && (
        <Modal isOpen={!!selectedFeedback} onClose={handleCloseModal}>
          <ModalTitle>Feedback details</ModalTitle>
          <ModalSection>
            <ModalLabel>Bot</ModalLabel>
            <ModalValue>
              {selectedFeedback.botName || selectedFeedback.botId}
            </ModalValue>
          </ModalSection>
          <ModalSection>
            <ModalLabel>User</ModalLabel>
            <ModalValue>{selectedFeedback.username}</ModalValue>
          </ModalSection>
          <ModalSection>
            <ModalLabel>Rating</ModalLabel>
            <ModalValue>{getRatingDisplay(selectedFeedback.rating)}</ModalValue>
          </ModalSection>
          <ModalSection>
            <ModalLabel>Message</ModalLabel>
            <ModalBody>{selectedFeedback.content}</ModalBody>
          </ModalSection>
          {selectedFeedback.feedback && (
            <ModalSection>
              <ModalLabel>Feedback</ModalLabel>
              <ModalText>{selectedFeedback.feedback}</ModalText>
            </ModalSection>
          )}
          <ModalSection>
            <ModalLabel>Date</ModalLabel>
            <ModalValue>{formatDate(selectedFeedback.createdAt)}</ModalValue>
          </ModalSection>
          {selectedFeedback.threadId && (
            <ModalSection>
              <ModalValue>
                <a
                  href={`/admin/thread/${selectedFeedback.threadId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button style={{ width: "100%" }}>View thread</Button>
                </a>
              </ModalValue>
            </ModalSection>
          )}
        </Modal>
      )}
    </>
  );
}

export default FeedbackTab;
